/**
 * SPIN TENET — Central Engine Registry & Adapter Catalog (Gate 2 Foundation)
 *
 * Provides a single centralized access point for simulation engine metadata,
 * capability inquiries, and adapter resolution.
 *
 * CRITICAL ARCHITECTURAL RULE:
 * Components must NEVER hard-code solver checks, engine-specific rules, or
 * assume that all engines support the same configurations.
 */

import type { EngineId, EngineDescriptor, RunSpecification } from '@contracts/index';
import type { EngineAdapter, AdapterValidationResult } from './adapter';
import { OpenFOAMAdapter } from './openfoamAdapter';
import { OpenLBAdapter } from './openlbAdapter';
import { PALMAdapter } from './palmAdapter';
import { WaLBerlaAdapter } from './walberlaAdapter';

class EngineRegistryClass {
  private adapters: Map<EngineId, EngineAdapter> = new Map();

  constructor() {
    this.registerAdapter(new OpenFOAMAdapter());
    this.registerAdapter(new OpenLBAdapter());
    this.registerAdapter(new PALMAdapter());
    this.registerAdapter(new WaLBerlaAdapter());
  }

  public registerAdapter(adapter: EngineAdapter): void {
    this.adapters.set(adapter.descriptor.id, adapter);
  }

  public getAllEngineDescriptors(): EngineDescriptor[] {
    return Array.from(this.adapters.values()).map((a) => a.descriptor);
  }

  public getEngineDescriptor(id: EngineId): EngineDescriptor | null {
    const adapter = this.adapters.get(id);
    return adapter ? adapter.descriptor : null;
  }

  public getEngineAdapter(id: EngineId): EngineAdapter | null {
    return this.adapters.get(id) || null;
  }

  public getAdapter(id: EngineId): EngineAdapter | null {
    return this.getEngineAdapter(id);
  }

  public validateSpecification(
    engineId: EngineId,
    specification: RunSpecification
  ): AdapterValidationResult {
    const adapter = this.getEngineAdapter(engineId);
    if (!adapter) {
      return {
        canRepresent: false,
        errors: [`ENGINE ADAPTER NOT FOUND: No adapter registered for engine '${engineId}'.`],
        warnings: []
      };
    }
    return adapter.validate(specification);
  }
}

export const EngineRegistry = new EngineRegistryClass();
